import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getModelFiles,
  upsertModelFile,
  deleteModelFile,
  getFileContent,
} from '@/services/modelFiles';
import type { ModelFile, ModelFileType } from '@/lib/supabase/types';

/**
 * Hook to fetch all files for a model
 */
export function useModelFiles(modelId: string | null) {
  return useQuery({
    queryKey: ['model-files', modelId],
    queryFn: () => (modelId ? getModelFiles(modelId) : Promise.resolve([])),
    enabled: !!modelId,
  });
}

/**
 * Hook to create or update a file
 */
export function useUpsertModelFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      modelId,
      filename,
      content,
      fileType,
    }: {
      modelId: string;
      filename: string;
      content: string | ArrayBuffer;
      fileType: ModelFileType;
    }) => {
      return upsertModelFile(modelId, filename, content, fileType);
    },
    onSuccess: (_, variables) => {
      // Invalidate the files query to refetch
      queryClient.invalidateQueries({
        queryKey: ['model-files', variables.modelId],
      });
    },
  });
}

/**
 * Hook to delete a file
 */
export function useDeleteModelFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fileId,
      modelId,
    }: {
      fileId: string;
      modelId: string;
    }) => {
      await deleteModelFile(fileId);
      return { fileId, modelId };
    },
    onSuccess: (_, variables) => {
      // Invalidate the files query to refetch
      queryClient.invalidateQueries({
        queryKey: ['model-files', variables.modelId],
      });
    },
  });
}

/**
 * Hook to download file content
 */
export function useFileContent(file: ModelFile | null) {
  return useQuery({
    queryKey: ['file-content', file?.id],
    queryFn: () => (file ? getFileContent(file) : Promise.resolve('')),
    enabled: !!file,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
